import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { selectIsAuthenticated, selectCurrentUser, selectAuthLoading, initializeAuth } from '../../store/authSlice';
import { UserRole } from '../../types';
import { AppDispatch } from '../../store';
import LoadingSpinner from '../LoadingSpinner';
import { hasPermission } from '../../utils/roleBasedAccess';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  requiredPermission?: string;
  fallbackPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermission,
  fallbackPath = '/login'
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentUser = useSelector(selectCurrentUser);
  const isLoading = useSelector(selectAuthLoading);

  useEffect(() => {
    // Initialize auth on app start - only run once on mount
    let mounted = true;
    
    if (mounted && !isAuthenticated && !isLoading) {
      dispatch(initializeAuth());
    }
    
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <LoadingSpinner />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (requiredRole && currentUser.role !== requiredRole) {
    // Redirect to unauthorized page or dashboard based on user role
    const redirectPath = currentUser.role === 'MANAGER' ? '/dashboard' : 
                        currentUser.role === 'RECRUITER' ? '/recruitment' : 
                        '/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  // Check permission-based access
  if (requiredPermission && !hasPermission(currentUser.role, requiredPermission as any)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;